import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';
import type { PixelEvent } from '@/events';

import type { IBehaviorTracker } from '../contracts/IBehaviorTracker';

type ListenerTarget = 'window' | 'document';

type ManagedListener = {
  readonly target: ListenerTarget;
  readonly type: string;
  readonly handler: EventListener;
  readonly options?: boolean | AddEventListenerOptions;
};

export abstract class BehaviorTrackerBase implements IBehaviorTracker {
  protected readonly context: PixelRuntimeContext;

  private active = false;

  private readonly managedListeners: ManagedListener[] = [];

  constructor(context: PixelRuntimeContext) {
    this.context = context;
  }

  init(): void {
    if (this.active) return;
    this.onInit();
    this.active = true;
  }

  destroy(): void {
    if (!this.active) return;
    this.onDestroy();
    this.removeAllManagedListeners();
    this.active = false;
  }

  protected abstract onInit(): void;

  protected abstract onDestroy(): void;

  protected isWindowAvailable(): boolean {
    return typeof window !== 'undefined';
  }

  protected isDocumentAvailable(): boolean {
    return typeof document !== 'undefined';
  }

  protected addWindowListener<K extends keyof WindowEventMap>(
    type: K,
    handler: (event: WindowEventMap[K]) => void,
    options?: boolean | AddEventListenerOptions
  ): void {
    if (!this.isWindowAvailable()) return;

    const listener = handler as EventListener;
    window.addEventListener(type, listener, options);
    this.managedListeners.push({
      target: 'window',
      type,
      handler: listener,
      options,
    });
  }

  protected addDocumentListener<K extends keyof DocumentEventMap>(
    type: K,
    handler: (event: DocumentEventMap[K]) => void,
    options?: boolean | AddEventListenerOptions
  ): void {
    if (!this.isDocumentAvailable()) return;

    const listener = handler as EventListener;
    document.addEventListener(type, listener, options);
    this.managedListeners.push({
      target: 'document',
      type,
      handler: listener,
      options,
    });
  }

  protected removeAllManagedListeners(): void {
    for (const entry of this.managedListeners) {
      if (entry.target === 'window' && this.isWindowAvailable()) {
        window.removeEventListener(entry.type, entry.handler, entry.options);
      } else if (entry.target === 'document' && this.isDocumentAvailable()) {
        document.removeEventListener(entry.type, entry.handler, entry.options);
      }
    }
    this.managedListeners.length = 0;
  }

  protected clearManagedTimeout(
    timer: ReturnType<typeof setTimeout> | null
  ): null {
    if (timer) clearTimeout(timer);
    return null;
  }

  protected dispatchEngagement<T extends { type: string }>(
    partial: T & { category: 'ENGAGEMENT' }
  ): void {
    const event = this.context.services.eventBuilder.build(
      partial
    ) as PixelEvent;
    this.context.services.dispatcher.dispatch(event);
  }
}
